import {
  Button,
  Col,
  DatePicker,
  Form,
  getDefaultSortOrder,
  Icons,
  Input,
  List,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  TextField,
  Tooltip,
  useTable,
} from "@pankod/refine-antd";
import { CrudFilters, IResourceComponentsProps } from "@pankod/refine-core";
import { Link } from "react-router-dom";
import {
  restoreAllPurchases,
  restorePurchases,
} from "apis/restorePurchases/restorePurchases";
import { ButtonExport } from "components/buttonExport";
import React, { useState, useEffect } from "react";
import { formatDateTime, getOrderNumber } from "utils";
import { message } from "antd";
import { ButtonPopover } from "components/buttonPopover";
import useMediaScreen from "hooks/useMediaScreen";
import axiosService from "services/axios.service";

interface OrderSearchParams {
  search?: string;
  itemName?: string;
  startDate?: string;
  endDate?: string;
  userId?: string;
}

export const OrdersList: React.FC<IResourceComponentsProps> = () => {
  const [advancedFilters, setAdvancedFilters] = useState({
    itemName: "",
    startDate: "",
    endDate: "",
    userId: "",
  });

  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [userSearchResults, setUserSearchResults] = useState<any[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [userSearchValue, setUserSearchValue] = useState("");

  // Modal state for viewing user orders
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalUser, setModalUser] = useState<any>(null);
  const [modalOrders, setModalOrders] = useState<any[]>([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalPagination, setModalPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Debounced user search function
  const searchUsers = React.useCallback(async (searchValue: string) => {
    if (!searchValue || searchValue.length < 2) {
      setUserSearchResults([]);
      return;
    }

    setUserSearchLoading(true);
    try {
      // Build query params matching backend format
      // Use large perPage to get all results (no pagination for dropdown)
      const queryParams = new URLSearchParams({
        page: "1",
        perPage: "100", // Get more results for dropdown
        search: searchValue,
      });

      // Use axiosService (project standard) instead of fetch
      const response = await axiosService.get(
        `/v1/admin/users?${queryParams.toString()}`
      );

      const users = response?.data?.data?.data || [];
      setUserSearchResults(users);
    } catch (error) {
      console.error("Error searching users:", error);
      setUserSearchResults([]);
    } finally {
      setUserSearchLoading(false);
    }
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchUsers(userSearchValue);
    }, 500);

    return () => clearTimeout(timer);
  }, [userSearchValue, searchUsers]);

  // Custom format for user options to show email and username clearly
  const formattedUserOptions = userSearchResults.map((user: any) => {
    // Always show email first
    let label = user.email;

    // Add username if available
    if (user.username) {
      label = `${user.email} (@${user.username})`;
    }
    // Or add full name if available
    else if (user.firstName && user.lastName) {
      label = `${user.email} (${user.firstName} ${user.lastName})`;
    }

    return {
      label,
      value: user._id,
      user, // Keep full user data for reference
    };
  });

  const { tableProps, sorter, searchFormProps }: any = useTable<any>({
    resource: "orders",
    onSearch: (params) => {
      const searchParams = params as OrderSearchParams;
      const filters: CrudFilters = [];

      // Format dates to YYYY-MM-DD if they exist
      let formattedStartDate = "";
      let formattedEndDate = "";

      if (searchParams.startDate) {
        const startDate: any = searchParams.startDate;
        // If it's a moment/dayjs object, format it
        if (typeof startDate === "object" && startDate.format) {
          formattedStartDate = startDate.format("YYYY-MM-DD");
        } else if (typeof startDate === "string") {
          formattedStartDate = startDate;
        }
      }

      if (searchParams.endDate) {
        const endDate: any = searchParams.endDate;
        // If it's a moment/dayjs object, format it
        if (typeof endDate === "object" && endDate.format) {
          formattedEndDate = endDate.format("YYYY-MM-DD");
        } else if (typeof endDate === "string") {
          formattedEndDate = endDate;
        }
      }

      // Build filter value object (matching project standard like users page)
      const filterValue: any = {
        search: searchParams.search || "",
        itemName: searchParams.itemName || "",
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        userId: searchParams.userId || "",
      };

      filters.push({
        field: "search",
        operator: "in",
        value: filterValue,
      });

      return filters;
    },
  });

  const current = tableProps?.pagination?.current;
  const pageSize = tableProps?.pagination?.pageSize;
  const { isMobile } = useMediaScreen();

  const handleRestore = async (item: any) => {
    const trackIds =
      item.tracks.length > 0
        ? item.tracks.map((i: { _id: string }) => i._id)
        : [];
    const releaseIds =
      item.releases.length > 0
        ? item.releases.map((i: { _id: string }) => i._id)
        : [];
    const payload = {
      trackIds: trackIds,
      releaseIds: releaseIds,
      userId: item.user._id,
    };
    const resource = "orders/restore";
    const res = await restorePurchases(payload, resource);

    if (res?.statusCode === 200) {
      message.success(res?.message);
    } else message.warning(res?.message || "Something went wrong.");
    return res;
  };

  const handleRestoreAllPurchases = async () => {
    const resource = "orders/restoreAllPurchases";
    const res = await restoreAllPurchases(resource);

    if (res?.statusCode === 200) {
      message.success(res?.message);
    } else message.warning(res?.message || "Something went wrong.");
  };

  // Load user orders for modal
  const loadUserOrders = async (
    userId: string,
    page: number = 1,
    pageSize: number = 10
  ) => {
    setModalLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        perPage: pageSize.toString(),
        userId: userId,
      });

      const response = await axiosService.get(
        `/v1/admin/orders?${queryParams.toString()}`
      );

      const data = response?.data?.data;
      setModalOrders(data?.data || []);
      setModalPagination({
        current: data?.currentPage || page,
        pageSize: data?.perPage || pageSize,
        total: data?.totalData || 0,
      });
    } catch (error) {
      console.error("Error loading user orders:", error);
      message.error("Failed to load user orders");
    } finally {
      setModalLoading(false);
    }
  };

  // Open modal to view user orders
  const handleViewUserOrders = async (user: any) => {
    setModalUser(user);
    setIsModalOpen(true);
    await loadUserOrders(user._id);
  };

  // Handle modal pagination change
  const handleModalPageChange = (page: number, pageSize?: number) => {
    if (modalUser) {
      loadUserOrders(modalUser._id, page, pageSize || modalPagination.pageSize);
    }
  };

  // Close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalUser(null);
    setModalOrders([]);
    setModalPagination({ current: 1, pageSize: 10, total: 0 });
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (advancedFilters.itemName) count++;
    if (advancedFilters.startDate) count++;
    if (advancedFilters.endDate) count++;
    if (advancedFilters.userId) count++;
    return count;
  };

  return (
    <List>
      <Row style={{ display: "block" }}>
        <div
          style={{
            display: "flex",
            gap: "5px",
            marginBottom: "15px",
            alignItems: "center",
          }}
        >
          <TextField
            value="Total:"
            style={{ color: "#626262", fontSize: "14px", fontWeight: 700 }}
          />
          <TextField
            value={tableProps?.pagination?.total?.length}
            style={{ color: "#626262", fontSize: "14px", fontWeight: 700 }}
          />
          {getActiveFiltersCount() > 0 && (
            <Tag color="blue">{getActiveFiltersCount()} active filter(s)</Tag>
          )}
        </div>

        <Col>
          <Form layout="vertical" {...searchFormProps}>
            {/* Basic Search */}
            <Form.Item
              label="Quick Search"
              name="search"
              style={{ width: isMobile ? "100%" : "100%" }}
              initialValue=""
            >
              <Input
                placeholder="Order ID, Status, Email, Paypal Email, Paypal Transaction ID"
                prefix={<Icons.SearchOutlined />}
                allowClear
              />
            </Form.Item>

            {/* Advanced Filters */}
            <div
              style={{
                background: "#f5f5f5",
                padding: "15px",
                borderRadius: "8px",
                marginBottom: "15px",
              }}
            >
              <h4 style={{ marginBottom: "15px", color: "#1890ff" }}>
                <Icons.FilterOutlined /> Advanced Filters
              </h4>

              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={12}>
                  <Form.Item
                    label="Item Name (Track/Multipack)"
                    name="itemName"
                    tooltip="Search for orders containing specific tracks or multipacks (e.g., '100 vol. 3', 'multipack')"
                  >
                    <Input
                      placeholder="e.g., 100 vol. 3, multipack, track name..."
                      prefix={<Icons.FileSearchOutlined />}
                      allowClear
                      onChange={(e) =>
                        setAdvancedFilters({
                          ...advancedFilters,
                          itemName: e.target.value,
                        })
                      }
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    label="Start Date"
                    name="startDate"
                    tooltip="Filter orders from this date (YYYY-MM-DD)"
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      format="YYYY-MM-DD"
                      placeholder="Start date"
                      onChange={(date) => {
                        if (!date) {
                          setAdvancedFilters({
                            ...advancedFilters,
                            startDate: "",
                          });
                          return;
                        }
                        // Format moment/dayjs object to YYYY-MM-DD
                        const formattedDate = date.format("YYYY-MM-DD");
                        setAdvancedFilters({
                          ...advancedFilters,
                          startDate: formattedDate,
                        });
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    label="End Date"
                    name="endDate"
                    tooltip="Filter orders until this date (YYYY-MM-DD)"
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      format="YYYY-MM-DD"
                      placeholder="End date"
                      onChange={(date) => {
                        if (!date) {
                          setAdvancedFilters({
                            ...advancedFilters,
                            endDate: "",
                          });
                          return;
                        }
                        // Format moment/dayjs object to YYYY-MM-DD
                        const formattedDate = date.format("YYYY-MM-DD");
                        setAdvancedFilters({
                          ...advancedFilters,
                          endDate: formattedDate,
                        });
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={12}>
                  <Form.Item
                    label="User (Search by Email/Username/ID)"
                    name="userId"
                    tooltip="Search by email, username, or paste User ID directly"
                  >
                    <Select
                      value={advancedFilters.userId || undefined}
                      options={formattedUserOptions}
                      placeholder="Type email, username, or paste User ID..."
                      showSearch
                      allowClear
                      filterOption={false}
                      loading={userSearchLoading}
                      notFoundContent={
                        userSearchLoading
                          ? "Loading..."
                          : userSearchValue && userSearchValue.length >= 2
                          ? "No users found"
                          : "Type at least 2 characters to search"
                      }
                      onChange={(value, option: any) => {
                        setAdvancedFilters({
                          ...advancedFilters,
                          userId: value as string,
                        });
                        // Save selected user info
                        if (option && option.user) {
                          setSelectedUser(option.user);
                        } else {
                          setSelectedUser(null);
                        }
                      }}
                      onClear={() => {
                        setSelectedUser(null);
                        setUserSearchValue("");
                        setUserSearchResults([]);
                      }}
                      onSearch={(value) => {
                        setUserSearchValue(value);
                      }}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>

                  {/* Display selected user info */}
                  {selectedUser && (
                    <div
                      style={{
                        marginTop: "-10px",
                        marginBottom: "10px",
                        padding: "8px 12px",
                        background: "#e6f7ff",
                        border: "1px solid #91d5ff",
                        borderRadius: "4px",
                        fontSize: "12px",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                          color: "#1890ff",
                          marginBottom: "4px",
                        }}
                      >
                        <Icons.CheckCircleOutlined /> Selected User:
                      </div>
                      <div>
                        <strong>Email:</strong> {selectedUser.email}
                      </div>
                      {selectedUser.username && (
                        <div>
                          <strong>Username:</strong> {selectedUser.username}
                        </div>
                      )}
                      {(selectedUser.firstName || selectedUser.lastName) && (
                        <div>
                          <strong>Name:</strong> {selectedUser.firstName}{" "}
                          {selectedUser.lastName}
                        </div>
                      )}
                      <div>
                        <strong>User ID:</strong> {selectedUser._id}
                      </div>
                    </div>
                  )}
                </Col>
              </Row>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "10px",
              }}
            >
              <Space>
                <Form.Item style={{ margin: 0 }}>
                  <Button
                    htmlType="submit"
                    type="primary"
                    icon={<Icons.SearchOutlined />}
                  >
                    Apply Filters
                  </Button>
                </Form.Item>
                <Form.Item style={{ margin: 0 }}>
                  <Button
                    onClick={() => {
                      searchFormProps.form?.resetFields();
                      setAdvancedFilters({
                        itemName: "",
                        startDate: "",
                        endDate: "",
                        userId: "",
                      });
                      setSelectedUser(null);
                      setUserSearchValue("");
                      setUserSearchResults([]);
                    }}
                    icon={<Icons.ClearOutlined />}
                  >
                    Reset Filters
                  </Button>
                </Form.Item>
              </Space>
              <ButtonExport path="orders" />
            </div>
          </Form>
        </Col>

        <Col>
          <div
            style={{
              display: "flex",
              width: "100%",
            }}
          >
            <div
              style={{
                marginLeft: "auto",
                marginBottom: "20px",
              }}
            >
              <ButtonPopover
                text="RESTORE ALL PURCHASES"
                title="Do you want to restore all purchases ?"
                onclickYes={handleRestoreAllPurchases}
              />
            </div>
          </div>
        </Col>

        <Col>
          <Table {...tableProps} rowKey="id" scroll={{ x: 1500 }}>
            <Table.Column
              dataIndex="_id"
              key="_id"
              title="Index"
              width={70}
              fixed="left"
              render={(value, item, index) => (
                <TextField value={getOrderNumber(current, pageSize, index)} />
              )}
            />
            <Table.Column
              dataIndex="createdAt"
              key="createdAt"
              title="Date"
              width={180}
              render={(value) => <TextField value={formatDateTime(value)} />}
              defaultSortOrder={getDefaultSortOrder("createdAt", sorter)}
              sorter
            />
            <Table.Column
              dataIndex="orderId"
              key="orderId"
              title="Order"
              width={150}
              render={(value) => <TextField value={value} />}
              defaultSortOrder={getDefaultSortOrder("orderId", sorter)}
              sorter
            />
            <Table.Column
              dataIndex="user"
              key="user"
              title="User"
              width={200}
              render={(value, item: any) => (
                <Space direction="vertical" size={0}>
                  <TextField value={item?.user?.email} />
                  <Tooltip title="View all orders from this user">
                    <Button
                      type="link"
                      size="small"
                      icon={<Icons.UserOutlined />}
                      onClick={() => handleViewUserOrders(item?.user)}
                      style={{ fontSize: "12px", padding: 0 }}
                    >
                      View User Orders
                    </Button>
                  </Tooltip>
                </Space>
              )}
              defaultSortOrder={getDefaultSortOrder("email", sorter)}
              sorter
            />
            <Table.Column
              dataIndex="paypalEmail"
              key="paypalEmail"
              title="Paypal Email"
              width={180}
              render={(value) => <TextField value={value} />}
              defaultSortOrder={getDefaultSortOrder("paypalEmail", sorter)}
              sorter
            />
            <Table.Column
              dataIndex="tracks"
              key="tracks"
              title="Tracks"
              width={100}
              render={(value, item: any) => (
                <Link to={`/orders/show/${item._id}`}>
                  <TextField
                    style={{ color: "blue" }}
                    value={value.length > 0 ? value.length : ""}
                  />
                </Link>
              )}
              defaultSortOrder={getDefaultSortOrder("tracks", sorter)}
              sorter
            />
            <Table.Column
              dataIndex="trackDiscount"
              key="trackDiscount"
              title="T-DC%"
              width={100}
              render={(value, item: any) => (
                <TextField
                  value={
                    item.tracks.length > 0 ? `${value.toFixed(2)}% OFF` : ""
                  }
                />
              )}
              defaultSortOrder={getDefaultSortOrder("trackDiscount", sorter)}
              sorter
            />
            <Table.Column
              dataIndex="trackCost"
              key="trackCost"
              title="T-COST"
              width={100}
              render={(value, item: any) => (
                <TextField
                  value={item.tracks.length > 0 ? `$${value.toFixed(2)}` : ""}
                />
              )}
              defaultSortOrder={getDefaultSortOrder("trackCost", sorter)}
              sorter
            />
            <Table.Column
              dataIndex="releases"
              key="releases"
              title="Releases"
              width={100}
              render={(value, item: any) => (
                <Link to={`/orders/show/${item._id}`}>
                  <TextField
                    style={{ color: "blue" }}
                    value={value.length > 0 ? value.length : ""}
                  />
                </Link>
              )}
              defaultSortOrder={getDefaultSortOrder("releases.length", sorter)}
              sorter
            />
            <Table.Column
              dataIndex="releaseDiscount"
              key="releaseDiscount"
              title="R-DC%"
              width={100}
              render={(value, item: any) => (
                <TextField
                  value={
                    item.releases.length > 0 ? `${value.toFixed(2)}% OFF` : ""
                  }
                />
              )}
              defaultSortOrder={getDefaultSortOrder("releaseDiscount", sorter)}
              sorter
            />
            <Table.Column
              dataIndex="releaseCost"
              key="releaseCost"
              title="R-COST"
              width={100}
              render={(value) => (
                <TextField value={value === 0 ? "" : `$${value.toFixed(2)}`} />
              )}
              defaultSortOrder={getDefaultSortOrder("releaseCost", sorter)}
              sorter
            />
            <Table.Column
              dataIndex="status"
              key="status"
              title="Status"
              width={120}
              render={(value) => (
                <Tag
                  color={
                    value === "COMPLETED"
                      ? "green"
                      : value === "PENDING"
                      ? "orange"
                      : "red"
                  }
                >
                  {value}
                </Tag>
              )}
              defaultSortOrder={getDefaultSortOrder("status", sorter)}
              sorter
            />
            <Table.Column
              dataIndex="totalCost"
              key="totalCost"
              title="Total Cost"
              width={120}
              render={(value) => (
                <TextField
                  value={`$${value.toFixed(2)}`}
                  style={{ fontWeight: 600 }}
                />
              )}
              defaultSortOrder={getDefaultSortOrder("totalCost", sorter)}
              sorter
            />
            <Table.Column
              dataIndex=""
              key="actions"
              title="Actions"
              width={120}
              fixed="right"
              render={(value, item: any) => (
                <Space>
                  <Tooltip title="View order details">
                    <Link to={`/orders/show/${item._id}`}>
                      <Button size="small" icon={<Icons.EyeOutlined />} />
                    </Link>
                  </Tooltip>
                  {item?.status !== "DISPUTED" && (
                    <ButtonPopover
                      text="RESTORE"
                      title="Do you want to restore this purchase ?"
                      onclickYes={() => handleRestore(item)}
                    />
                  )}
                </Space>
              )}
            />
          </Table>
        </Col>
      </Row>

      {/* User Orders Modal */}
      <Modal
        title={
          modalUser ? (
            <div style={{ fontSize: 16 }}>
              <Icons.UserOutlined /> User Information: {modalUser.email}
              {modalUser.username && ` (@${modalUser.username})`}
            </div>
          ) : (
            "User Information"
          )
        }
        visible={isModalOpen}
        onCancel={handleCloseModal}
        footer={null}
        width={"95vw !important"}
        centered
        style={{ top: 20, maxWidth: "95vw" }}
        bodyStyle={{ maxHeight: "85vh", overflowY: "auto", padding: 24 }}
      >
        {/* User Information Section */}
        {modalUser && (
          <div
            style={{
              marginBottom: 24,
              padding: 20,
              background: "#f5f5f5",
              borderRadius: 8,
            }}
          >
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  {modalUser.avatar && (
                    <img
                      src={modalUser.avatar}
                      alt="avatar"
                      style={{
                        width: 80,
                        height: 80,
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                  )}
                  <div style={{ flex: 1 }}>
                    <h2 style={{ margin: 0, marginBottom: 8 }}>
                      {modalUser.firstName || modalUser.lastName
                        ? `${modalUser.firstName || ""} ${
                            modalUser.lastName || ""
                          }`.trim()
                        : modalUser.email}
                    </h2>
                    <div style={{ color: "#666", marginBottom: 4 }}>
                      <Icons.MailOutlined /> {modalUser.email}
                    </div>
                    {modalUser.username && (
                      <div style={{ color: "#666", marginBottom: 4 }}>
                        <Icons.UserOutlined /> @{modalUser.username}
                      </div>
                    )}
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div>
                  <div style={{ color: "#999", fontSize: 12 }}>Role</div>
                  <div style={{ fontWeight: 600 }}>
                    {modalUser.role === "admin"
                      ? "Admin"
                      : modalUser.isContributor
                      ? "Contributor"
                      : "User"}
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div>
                  <div style={{ color: "#999", fontSize: 12 }}>
                    Stars Remaining
                  </div>
                  <div style={{ fontWeight: 600 }}>
                    {modalUser.starsRemaining || 0}
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div>
                  <div style={{ color: "#999", fontSize: 12 }}>Status</div>
                  <div>
                    <Tag color={modalUser.isEnabled ? "green" : "red"}>
                      {modalUser.isEnabled ? "Active" : "Blocked"}
                    </Tag>
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div>
                  <div style={{ color: "#999", fontSize: 12 }}>
                    Member Since
                  </div>
                  <div style={{ fontWeight: 600 }}>
                    {formatDateTime(modalUser.createdAt)}
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        )}

        {/* Orders Section */}
        <div style={{ marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>
            <Icons.ShoppingOutlined /> Order History ({modalPagination.total})
          </h3>
        </div>
        <Table
          dataSource={modalOrders}
          loading={modalLoading}
          rowKey="_id"
          pagination={{
            current: modalPagination.current,
            pageSize: modalPagination.pageSize,
            total: modalPagination.total,
            onChange: handleModalPageChange,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} orders`,
          }}
          scroll={{ x: 1000 }}
        >
          <Table.Column
            dataIndex="createdAt"
            key="createdAt"
            title="Date"
            width={150}
            render={(value) => <TextField value={formatDateTime(value)} />}
          />
          <Table.Column
            dataIndex="orderId"
            key="orderId"
            title="Order ID"
            width={120}
            render={(value) => <TextField value={value} />}
          />
          <Table.Column
            dataIndex="tracks"
            key="tracks"
            title="Tracks"
            width={80}
            render={(value) => <TextField value={value?.length || 0} />}
          />
          <Table.Column
            dataIndex="releases"
            key="releases"
            title="Releases"
            width={80}
            render={(value) => <TextField value={value?.length || 0} />}
          />
          <Table.Column
            dataIndex="status"
            key="status"
            title="Status"
            width={100}
            render={(value) => (
              <Tag
                color={
                  value === "COMPLETED"
                    ? "green"
                    : value === "PENDING"
                    ? "orange"
                    : "red"
                }
              >
                {value}
              </Tag>
            )}
          />
          <Table.Column
            dataIndex="totalCost"
            key="totalCost"
            title="Total"
            width={100}
            render={(value) => (
              <TextField
                value={`$${value?.toFixed(2)}`}
                style={{ fontWeight: 600 }}
              />
            )}
          />
          <Table.Column
            dataIndex=""
            key="actions"
            title="Actions"
            width={100}
            fixed="right"
            render={(value, item: any) => (
              <Space>
                <Tooltip title="View order details">
                  <Link to={`/orders/show/${item._id}`} target="_blank">
                    <Button size="small" icon={<Icons.EyeOutlined />} />
                  </Link>
                </Tooltip>
              </Space>
            )}
          />
        </Table>
      </Modal>
    </List>
  );
};
